import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendCode, verifyCode } from '../api/client';
import { PetlingsLogo } from '../components/icons';
import {
  appleSignInAvailable,
  prepareAppleSignIn,
  signInWithApple,
} from '../auth/appleSignIn';
import './Login.css';

// The backend's floor for a new password (MIN_PASSWORD in auth.py).
const MIN_PASSWORD = 8;

function AppleLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 814 1000" aria-hidden="true" fill="currentColor">
      <path d="M788 341c-6 4-107 61-107 188 0 147 129 199 133 200-1 3-21 71-68 140-42 61-86 122-153 122s-84-39-161-39c-75 0-102 40-163 40s-104-57-153-127C69 782 23 661 23 546c0-184 120-282 238-282 63 0 115 41 154 41 38 0 97-44 168-44 27 0 124 3 190 80zM558 169c29-35 50-83 50-131 0-7-1-14-2-19-48 2-104 32-138 71-27 30-52 78-52 127 0 7 1 15 2 17 3 1 8 1 13 1 43 0 97-29 127-66z"/>
    </svg>
  );
}

function MicrosoftLogo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export default function Login() {
  const { login, loginWithToken, loginWithApple, register, isAuthenticated } = useAuth();
  // Apple's popup must open straight from the click, so the script and the
  // nonce are made ready before it.
  const applePrepared = useRef(null);
  const prepareApple = useCallback(() => {
    if (!appleSignInAvailable()) return;
    prepareAppleSignIn()
      .then((prepared) => { applePrepared.current = prepared; })
      .catch(() => { applePrepared.current = null; });
  }, []);
  useEffect(() => { prepareApple(); }, [prepareApple]);

  const handleApple = async () => {
    setError('');
    const prepared = applePrepared.current;
    if (!prepared) {
      setError('Sign in with Apple is still loading. Please try again in a moment.');
      prepareApple();
      return;
    }
    try {
      const signedIn = await signInWithApple(prepared);
      if (!signedIn) return; // closed the popup
      setLoading(true);
      const result = await loginWithApple(signedIn);
      setLoading(false);
      if (result.success) navigate('/pets', { replace: true });
      else setError(result.error);
    } catch {
      setLoading(false);
      setError('Sign in with Apple failed. Please try again.');
    } finally {
      // A nonce is good for one attempt.
      prepareApple();
    }
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [registerStep, setRegisterStep] = useState('form'); // 'form' | 'verify'
  const [regCode, setRegCode] = useState('');
  const [isForgot, setIsForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState('email'); // 'email' | 'code'
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [showMcModal, setShowMcModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenLogin, setTokenLogin] = useState(false);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendCode = async (purpose = 'reset') => {
    if (resendCooldown > 0 || !username.trim()) return;
    setError('');
    try {
      await sendCode(username.trim(), purpose);
      setResendCooldown(60);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend code.');
    }
  };

  // Auto-login with token from URL (sent by the Minecraft mod)
  useEffect(() => {
    const token = searchParams.get('token');
    if (token && !isAuthenticated && !tokenLogin) {
      setTokenLogin(true);
      setLoading(true);
      setError('');
      loginWithToken(token).then((result) => {
        setLoading(false);
        if (result.success) {
          // Clean the token from URL and redirect
          navigate('/pets', { replace: true });
        } else {
          setError('Session expired. Please sign in manually.');
          // Clean URL
          navigate('/login', { replace: true });
        }
      });
    }
  }, [searchParams, isAuthenticated, tokenLogin, loginWithToken, navigate]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated && !loading) navigate('/pets');
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setForgotSuccess('');

    if (isForgot) {
      setLoading(true);
      if (forgotStep === 'email') {
        if (!username.trim()) { setError('Enter your email.'); setLoading(false); return; }
        try {
          await sendCode(username.trim(), 'reset');
          setForgotStep('code');
          setResendCooldown(60);
        } catch (err) {
          setError(err.response?.data?.detail || 'Could not send code.');
        }
      } else {
        if (!resetCode.trim() || !newPassword.trim()) {
          setError('Enter the code and your new password.'); setLoading(false); return;
        }
        try {
          await verifyCode(username.trim(), resetCode.trim(), newPassword);
          setForgotSuccess('Password reset! You can now sign in.');
          setIsForgot(false);
          setForgotStep('email');
          setResetCode('');
          setNewPassword('');
        } catch (err) {
          setError(err.response?.data?.detail || 'Invalid or expired code.');
        }
      }
      setLoading(false);
      return;
    }

    if (!username.trim() || !password.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    if (isRegister && registerStep === 'form') {
      // Step 1: send verification code
      setLoading(true);
      try {
        await sendCode(username.trim(), 'verify');
        setRegisterStep('verify');
        setResendCooldown(60);
      } catch (err) {
        setError(err.response?.data?.detail || 'Could not send verification code.');
      }
      setLoading(false);
      return;
    }

    if (isRegister && registerStep === 'verify') {
      // Step 2: register with code
      if (!regCode.trim()) { setError('Enter the verification code.'); return; }
      setLoading(true);
      const result = await register(username.trim(), password, regCode.trim());
      setLoading(false);
      if (result.success) {
        navigate('/pets');
      } else {
        setError(result.error);
      }
      return;
    }

    // Normal login
    setLoading(true);
    const result = await login(username.trim(), password);
    setLoading(false);

    if (result.success) {
      navigate('/pets');
    } else {
      setError(result.error);
    }
  };

  // Show loading while token auth is in progress
  if (tokenLogin && loading) {
    return (
      <div className="login-page">
        <div className="login-card card">
          <div className="login-header">
            <PetlingsLogo size={40} color="var(--magenta-primary)" />
            <h1>Signing you in...</h1>
            <p className="login-subtitle">Verifying your Minecraft session.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleMicrosoftLogin = () => {
    const clientId = process.env.REACT_APP_MS_CLIENT_ID;
    if (!clientId) {
      setError('Microsoft sign-in is not configured yet.');
      return;
    }
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
    const scope = encodeURIComponent('XboxLive.signin XboxLive.offline_access');
    const msAuthUrl = `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${scope}&response_mode=query&prompt=select_account`;
    window.location.href = msAuthUrl;
  };

  return (
    <div className="login-page">
      <div className="login-card card">
        <div className="login-header">
          <PetlingsLogo size={40} color="var(--magenta-primary)" />
          <h1>Welcome to Petlings</h1>
          <p className="login-subtitle">
            Sign in to access your pets from anywhere.
          </p>
        </div>

        {searchParams.get('deleted') && (
          <p className="login-deleted-notice">Your account has been deleted.</p>
        )}

        {/* Sign in with Apple, first, as App Review expects alongside other
            third-party sign-in. Hidden until the Services ID is configured. */}
        {appleSignInAvailable() && (
          <button
            type="button"
            className="apple-login-btn"
            onClick={handleApple}
            disabled={loading}
            data-testid="apple-login"
          >
            <AppleLogo />
            <span>Sign in with Apple</span>
          </button>
        )}

        {/* Sign in with Minecraft - link via mod */}
        <button
          type="button"
          className="mc-login-btn"
          onClick={() => setShowMcModal(true)}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" className="mc-login-icon">
            <rect x="1" y="1" width="20" height="20" rx="3" fill="#8B6940" />
            <rect x="1" y="1" width="20" height="10" rx="3" fill="#5D8C3E" />
            <rect x="3" y="3" width="4" height="3" rx="1" fill="#4A7030" />
            <rect x="13" y="2" width="3" height="2" rx="0.5" fill="#4A7030" />
            <rect x="5" y="12" width="3" height="3" rx="0.5" fill="#7A5C35" />
            <rect x="10" y="14" width="4" height="2" rx="0.5" fill="#7A5C35" />
            <rect x="16" y="12" width="2" height="4" rx="0.5" fill="#7A5C35" />
          </svg>
          <span>Sign in with Minecraft</span>
        </button>

        <p className="mc-login-hint">
          Link your Minecraft account through the Petlings mod.
        </p>

        {/* Minecraft sign-in modal */}
        {showMcModal && (
          <div className="mc-modal-overlay" onClick={() => setShowMcModal(false)}>
            <div className="mc-modal card" onClick={(e) => e.stopPropagation()}>
              <h2>Sign in with Minecraft</h2>
              <p className="mc-modal-desc">
                Connect your Minecraft account through the Petlings mod. Choose how you'd like to link:
              </p>

              <div className="mc-modal-options">
                <div className="mc-modal-option mc-modal-primary">
                  <div className="mc-modal-option-icon">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect x="4" y="4" width="20" height="20" rx="3" fill="#5D8C3E" />
                      <text x="14" y="18" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="monospace">G</text>
                    </svg>
                  </div>
                  <div>
                    <h3>Link from the mod</h3>
                    <p>Open Petlings in Minecraft, go to Account, click "Visit Petlings Home". You'll be signed in automatically.</p>
                  </div>
                  <span className="mc-modal-tag">Recommended</span>
                </div>

                <div className="mc-modal-option">
                  <div className="mc-modal-option-icon">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect x="6" y="6" width="16" height="16" rx="2" fill="var(--bg-secondary)" stroke="var(--border-light)" strokeWidth="1.5" />
                      <text x="14" y="18" textAnchor="middle" fill="var(--text-secondary)" fontSize="10" fontWeight="bold" fontFamily="monospace">123</text>
                    </svg>
                  </div>
                  <div>
                    <h3>Use a link code</h3>
                    <p>Create an account with email first, then generate a link code from your Profile page and enter it in the mod.</p>
                  </div>
                </div>

                <div className="mc-modal-option">
                  <div className="mc-modal-option-icon">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <circle cx="14" cy="14" r="10" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" />
                      <path d="M10 14H18M14 10V18" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div>
                    <h3>Don't have the mod?</h3>
                    <p>
                      Download Petlings from{' '}
                      <a href="https://modrinth.com/mod/petlings" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--magenta-primary)' }}>Modrinth</a>
                      {' '}or{' '}
                      <a href="https://www.curseforge.com/minecraft/mc-mods/petlings" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--magenta-primary)' }}>CurseForge</a>
                      {' '}for Minecraft (Fabric 1.21.11 & 26.1+), then use the link above.
                    </p>
                  </div>
                </div>
              </div>

              <button className="btn btn-ghost mc-modal-close" onClick={() => setShowMcModal(false)}>
                Got it
              </button>
            </div>
          </div>
        )}

        {error && <div className="login-error">{error}</div>}

        {/* Divider */}
        <div className="login-divider">
          <span>or sign in with email</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {isForgot ? (
            // Forgot password flow
            forgotStep === 'email' ? (
              <>
                <p className="login-form-hint">Enter your email and we'll send you a reset code.</p>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="you@example.com" autoComplete="email" disabled={loading} />
                </div>
                <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </>
            ) : (
              <>
                <p className="login-form-hint">We sent a 6-digit code to <strong>{username}</strong></p>
                <div className="form-group">
                  <label htmlFor="code">Verification Code</label>
                  <input id="code" type="text" value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                    placeholder="ABC123" maxLength={6} autoComplete="one-time-code"
                    disabled={loading} className="login-code-input" />
                </div>
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <input id="new-password" type="password" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters" autoComplete="new-password"
                    minLength={MIN_PASSWORD} required disabled={loading} />
                </div>
                <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
                <p className="login-resend">
                  Didn't get the code?{' '}
                  {resendCooldown > 0 ? (
                    <span className="login-resend-wait">Resend in {resendCooldown}s</span>
                  ) : (
                    <button type="button" className="link-btn" onClick={() => handleResendCode('reset')}>
                      Resend code
                    </button>
                  )}
                </p>
              </>
            )
          ) : (
            // Normal login/register
            <>
              {isRegister && registerStep === 'verify' ? (
                // Registration step 2: enter verification code
                <>
                  <p className="login-form-hint">We sent a code to <strong>{username}</strong>. Enter it below to verify your email.</p>
                  <div className="form-group">
                    <label htmlFor="reg-code">Verification Code</label>
                    <input id="reg-code" type="text" value={regCode}
                      onChange={(e) => setRegCode(e.target.value.toUpperCase())}
                      placeholder="ABC123" maxLength={6} autoComplete="one-time-code"
                      disabled={loading} className="login-code-input" />
                  </div>
                  <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                    {loading ? 'Creating...' : 'Verify & Create Account'}
                  </button>
                  <p className="login-resend">
                    Didn't get the code?{' '}
                    {resendCooldown > 0 ? (
                      <span className="login-resend-wait">Resend in {resendCooldown}s</span>
                    ) : (
                      <button type="button" className="link-btn" onClick={() => handleResendCode('verify')}>
                        Resend code
                      </button>
                    )}
                  </p>
                </>
              ) : (
                // Login or registration step 1
                <>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input id="email" type="email" value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="you@example.com" autoComplete="email" disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input id="password" type="password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isRegister ? 'At least 8 characters' : 'Your password'}
                      // New passwords need 8 characters (the backend refuses fewer);
                      // an older, shorter password still signs in.
                      minLength={isRegister ? MIN_PASSWORD : undefined}
                      autoComplete={isRegister ? 'new-password' : 'current-password'} disabled={loading} />
                  </div>
                  <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                    {loading ? 'Please wait...' : isRegister ? 'Send Verification Code' : 'Sign In'}
                  </button>
                </>
              )}
            </>
          )}
        </form>

        {forgotSuccess && <div className="login-success">{forgotSuccess}</div>}

        <div className="login-switch">
          {isForgot ? (
            <p>
              <button className="link-btn" onClick={() => { setIsForgot(false); setForgotStep('email'); setError(''); }}>
                Back to sign in
              </button>
            </p>
          ) : isRegister ? (
            <p>
              Already have an account?{' '}
              <button className="link-btn" onClick={() => { setIsRegister(false); setError(''); }}>
                Sign in
              </button>
            </p>
          ) : (
            <p>
              <button className="link-btn" onClick={() => { setIsForgot(true); setError(''); setForgotSuccess(''); }}>
                Forgot password?
              </button>
              {' · '}
              <button className="link-btn" onClick={() => { setIsRegister(true); setRegisterStep('form'); setRegCode(''); setError(''); }}>
                Create account
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
